import React, { useState, useEffect, useRef } from 'react';
import OptinodeAILogo from '../common/OptinodeAILogo';
import './MarketingHeader.css';

interface DropdownItem {
  id: string;
  label: string;
  description?: string;
  href?: string;
  onClick?: () => void;
}

interface DropdownMenu {
  id: string;
  label: string;
  items: DropdownItem[];
}

interface MarketingHeaderProps {
  onLogin?: () => void;
  onGetStarted?: () => void;
  onNavigateHome?: () => void;
}

const MarketingHeader: React.FC<MarketingHeaderProps> = ({ onLogin, onGetStarted, onNavigateHome }) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dropdownMenus: DropdownMenu[] = [
    {
      id: 'what-we-do',
      label: 'What we do',
      items: [
        {
          id: 'warehouse-optimization',
          label: 'Warehouse Optimization',
          description: 'AI-powered inventory allocation and distribution optimization'
        },
        {
          id: 'demand-forecasting',
          label: 'Demand Forecasting',
          description: 'Predictive analytics for accurate demand planning'
        },
        {
          id: 'supply-chain-analytics',
          label: 'Supply Chain Analytics',
          description: 'Real-time insights and performance monitoring'
        },
        {
          id: 'sop-planning',
          label: 'S&OP Planning',
          description: 'Strategic sales and operations planning platform'
        }
      ]
    },
    {
      id: 'who-we-are',
      label: 'Who we are',
      items: [
        {
          id: 'about-us',
          label: 'About Optinode AI',
          description: 'Learn about our mission and vision'
        },
        {
          id: 'our-team',
          label: 'Our Team',
          description: 'Meet the experts behind our technology'
        },
        {
          id: 'careers',
          label: 'Careers',
          description: 'Join our growing team of innovators'
        },
        {
          id: 'news',
          label: 'News & Press',
          description: 'Latest company news and media coverage'
        }
      ]
    },
    {
      id: 'resources',
      label: 'Resources',
      items: [
        {
          id: 'documentation',
          label: 'Documentation',
          description: 'Complete API and user guides'
        },
        {
          id: 'case-studies',
          label: 'Case Studies',
          description: 'Real-world success stories and implementations'
        },
        {
          id: 'blog',
          label: 'Blog',
          description: 'Industry insights and best practices'
        },
        {
          id: 'webinars',
          label: 'Webinars',
          description: 'Educational sessions and product demos'
        },
        {
          id: 'support',
          label: 'Support Center',
          description: 'Get help and find answers to common questions'
        }
      ]
    },
    {
      id: 'pricing',
      label: 'Pricing',
      items: [
        {
          id: 'plans',
          label: 'View Plans',
          description: 'Compare our pricing tiers and features'
        },
        {
          id: 'calculator',
          label: 'ROI Calculator',
          description: 'Calculate your potential return on investment'
        },
        {
          id: 'enterprise',
          label: 'Enterprise Solutions',
          description: 'Custom pricing for large organizations'
        },
        {
          id: 'contact-sales',
          label: 'Contact Sales',
          description: 'Speak with our sales team'
        }
      ]
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const handleDropdownEnter = (menuId: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(menuId);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleDropdownItemClick = (item: DropdownItem) => {
    if (item.onClick) {
      item.onClick();
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
  };

  return (
    <header ref={headerRef} className="marketing-header" role="banner">
      <div className="header-container">
        {/* Logo */}
        <button 
          className="header-logo"
          onClick={onNavigateHome}
          aria-label="Navigate to home page"
        >
          <OptinodeAILogo width={40} height={40} className="logo-icon" />
          <span className="logo-text">Optinode AI</span>
        </button>

        {/* Desktop Navigation */}
        <nav className="desktop-nav" role="navigation" aria-label="Main navigation">
          <ul className="nav-menu">
            {dropdownMenus.map((menu) => (
              <li
                key={menu.id}
                className="nav-item"
                onMouseEnter={() => handleDropdownEnter(menu.id)}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  className={`nav-link ${activeDropdown === menu.id ? 'active' : ''}`}
                  aria-expanded={activeDropdown === menu.id}
                  aria-haspopup="true"
                >
                  {menu.label}
                  <span className="dropdown-arrow">▼</span>
                </button>
                
                {activeDropdown === menu.id && (
                  <div className="dropdown-menu" role="menu">
                    <div className="dropdown-content">
                      {menu.items.map((item) => (
                        <button
                          key={item.id}
                          className="dropdown-item"
                          role="menuitem"
                          onClick={() => handleDropdownItemClick(item)}
                        >
                          <div className="item-content">
                            <span className="item-label">{item.label}</span>
                            {item.description && (
                              <span className="item-description">{item.description}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Header Actions */}
        <div className="header-actions">
          <button
            className="btn btn-ghost"
            onClick={onLogin}
            aria-label="Log in to your account"
          >
            Log In
          </button>
          <button
            className="btn btn-primary"
            onClick={onGetStarted}
            aria-label="Get started with Optinode AI"
          >
            Get Started
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle mobile menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-content">
          {dropdownMenus.map((menu) => (
            <div key={menu.id} className="mobile-nav-section">
              <h3 className="mobile-nav-title">{menu.label}</h3>
              <ul className="mobile-nav-items">
                {menu.items.map((item) => (
                  <li key={item.id}>
                    <button
                      className="mobile-nav-item"
                      onClick={() => handleDropdownItemClick(item)}
                    >
                      <div className="mobile-item-content">
                        <span className="mobile-item-label">{item.label}</span>
                        {item.description && (
                          <span className="mobile-item-description">{item.description}</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          
          <div className="mobile-nav-actions">
            <button
              className="btn btn-ghost btn-full"
              onClick={() => {
                onLogin?.();
                setIsMobileMenuOpen(false);
              }}
            >
              Log In
            </button>
            <button
              className="btn btn-primary btn-full"
              onClick={() => {
                onGetStarted?.();
                setIsMobileMenuOpen(false);
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default MarketingHeader;